import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Layout,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
} from "antd";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "../../Components/Navbar/Navbar";
import PageHeader from "../../Components/PageHeader/PageHeader";
import {
  formatNumber,
  formatDate,
  formatString,
  handleChangePage,
  handleExportToExcel,
  handleExportToPDF,
  handlePageSizeChange,
} from "../../Utils/Common";
import CustomTable from "../../Components/Table/CustomTable";
import { getRequest, postRequest } from "../../Utils/ApiCalls";
import PersistentFooter from "../../Components/PersistentFooter/PersistentFooter";
import ErrorModal from "../../Components/CustomModals/ErrorModal";
import SuccessModal from "../../Components/CustomModals/SuccessModal";

const { Option } = Select;

const AdvancePayment = () => {
  const [loading,        setLoading]        = useState("");
  const [showModal,      setShowModal]      = useState("");
  const [filterConfig,   setFilterConfig]   = useState({ page: 1 });
  const [dataSize,       setDataSize]       = useState(0);
  const [form]                              = Form.useForm();
  const [selectedRow,    setSelectedRow]    = useState<any>({});
  const [dataSource,     setDataSource]     = useState<any[]>([]);
  const [companies,      setCompanies]      = useState<any[]>([]);
  const [errorMessage,   setErrorMessage]   = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const typeOptions = [
    { value: "cash",   label: "Cash"   },
    { value: "check",  label: "Check"  },
    { value: "online", label: "Online" },
  ];

  function clearFields() {
    setSelectedRow({});
    form.resetFields();
  }

  function handleFilterChange(value: any, key: string) {
    setFilterConfig((prev: any) => ({ ...prev, [key]: value, page: 1 }));
  }

  const columns = [
    {
      title: "Company",
      dataIndex: "company_name",
      key: "company_name",
    },
    {
      title: "Owner",
      dataIndex: "company_owner_name",
      key: "company_owner_name",
    },
    {
      title: "Amount Paid",
      dataIndex: "amount_paid",
      key: "amount_paid",
      align: "right" as any,
      render: (value: number) => <span>{formatNumber(value)}</span>,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (text: string) => (
        <span className="capitalize">{text ?? "—"}</span>
      ),
    },
    {
      title: "Added By",
      dataIndex: "added_by_name",
      key: "added_by_name",
    },
    {
      title: "Date",
      dataIndex: "added_on",
      key: "added_on",
      render: (date: any) => formatDate(date, "MMM DD, YYYY h:mm A"),
    },
  ];

  async function exportToExcel() {
    setLoading("export");
    try {
      const exportFilter = { ...filterConfig, page: "" };
      const response = await getRequest(`advance_payments/get`, exportFilter, true);
      if (response.success) {
        handleExportToExcel(response.data.data, columns, "AdvancePayment");
      } else {
        message.error("Failed to fetch data for export.");
      }
    } catch {
      message.error("Error exporting data.");
    } finally {
      setLoading("");
    }
  }

  async function exportToPDF() {
    setLoading("export");
    try {
      const exportFilter = { ...filterConfig, page: "", page_size: "" };
      const response = await getRequest(`advance_payments/get`, exportFilter, true);
      if (response.success) {
        const exportReadyData = response.data.data;
        handleExportToPDF(
          exportReadyData,
          columns,
          "AdvancePayment.pdf",
          {
            title: "List of Advance Payments",
            content: `Generated on ${new Date().toLocaleString()}`,
          },
          `Total Records: ${exportReadyData.length}`
        );
      } else {
        message.error("Failed to fetch data for PDF export.");
      }
    } catch {
      message.error("Error exporting PDF.");
    } finally {
      setLoading("");
    }
  }

  async function submitForm(data: any) {
    setLoading("submit");
    const response = await postRequest(
      `advance_payments/${showModal}`,
      { advance_payment_id: selectedRow.id, ...data },
      true
    );
    if (response.success) {
      setSuccessMessage(formatString(response.data.message));
      setTimeout(() => {
        clearFields();
        setShowModal("");
        setLoading("");
        setSuccessMessage("");
        fetchTableData();
      }, 1000);
    } else {
      setLoading("");
      setErrorMessage(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          `Failed to ${showModal} advance payment.`
      );
    }
  }

  async function deleteAdvancePayment() {
    const response = await postRequest(
      `advance_payments/delete`,
      { advance_payment_id: selectedRow.id },
      true
    );
    if (response.success) {
      setSuccessMessage(formatString(response.data.message));
      setTimeout(() => {
        setShowModal("");
        clearFields();
        setSuccessMessage("");
        fetchTableData();
      }, 1000);
    } else {
      setErrorMessage(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          "Failed to delete advance payment."
      );
    }
  }

  async function fetchTableData() {
    setDataSource([]);
    setLoading("fetch");
    const response = await getRequest(`advance_payments/get`, filterConfig, true);
    if (response.success) {
      setDataSize(response.data.size);
      setDataSource(response.data.data);
    }
    setLoading("");
  }

  async function fetchCompanies() {
    setCompanies([]);
    const response = await getRequest(`companies/get`, true);
    if (response.success) {
      setCompanies(response.data.data);
    }
  }

  useEffect(() => {
    fetchTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterConfig]);

  useEffect(() => {
    fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Layout className="page">
      <Helmet>
        <title>Agua Lab ERP System</title>
      </Helmet>
      <Navbar />
      <Layout>
        <Row className="px-3 pt-20 md:px-10 md:pb-10 md:pt-8 w-screen md:w-full pb-[50px] sm:pb-0 overflow-scroll text-textColor">
          <Col xs={24}>
            <PageHeader
              title="Advance Payment"
              withDateRangeFilter={false}
              withAddButton={true}
              addBtnText="Advance Payment"
              onCreate={() => setShowModal("create")}
              withExportDropdownButton={true}
              disableExportButton={dataSource?.length === 0}
              onExportToExcel={() => exportToExcel()}
              onExportToPDF={() => exportToPDF()}
              withSearchBtn={true}
              onSearch={(value: any) =>
                setFilterConfig((prev: any) => ({ ...prev, search: value }))
              }
            />
          </Col>

          {/* ── Filters ── */}
          <Col xs={24} className="mb-1">
            <span><strong>Filter By:</strong></span>
          </Col>
          <Col span={24} className="mb-3">
            <Row gutter={[10, 10]}>
              <Col xs={24} md={12} lg={5}>
                <Select
                  className="w-full"
                  showSearch
                  placeholder="Select company"
                  allowClear
                  onChange={(value) => handleFilterChange(value, "company_id")}
                  filterOption={(input: string, option?: any) =>
                    option?.children?.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  <Option value="">All Companies</Option>
                  {companies.map((company: any) => (
                    <Option key={company.id} value={company.id}>
                      {company.name}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} md={12} lg={5}>
                <Select
                  className="w-full"
                  placeholder="Filter by type"
                  allowClear
                  onChange={(value) => handleFilterChange(value, "type")}
                  options={typeOptions}
                />
              </Col>
            </Row>
          </Col>

          <Col span={24}>
            <div className="table-container overflow-auto">
              <div className="min-w-[600px]">
                <CustomTable
                  data={dataSource}
                  columns={columns}
                  rowKey="id"
                  loading={loading === "fetch"}
                  size="small"
                  pagination={{
                    current: filterConfig.page,
                    total: dataSize,
                    showSizeChanger: true,
                    onChange: (page: number) =>
                      handleChangePage(page, setFilterConfig),
                    onShowSizeChange: (_: number, size: number) =>
                      handlePageSizeChange(size, setFilterConfig),
                  }}
                  handleRowClick={(record: any) => {
                    setShowModal("update");
                    setSelectedRow(record);
                    form.setFieldsValue({ ...record });
                  }}
                />
              </div>
            </div>
          </Col>
        </Row>
      </Layout>

      {/* ── Create / Update Modal ── */}
      <Modal
        open={showModal === "create" || showModal === "update"}
        title={`${showModal === "create" ? "Add" : "Edit"} Advance Payment`}
        centered
        footer={null}
        onCancel={() => {
          clearFields();
          setShowModal("");
        }}
        width={600}
        maskClosable={false}
      >
        <div className="max-h-[70vh] overflow-y-auto pr-2">
          <Form form={form} onFinish={submitForm} layout="vertical">
            <Row gutter={[10, 10]} className="mt-2">

              {/* Company */}
              <Col xs={24} md={24}>
                <Form.Item
                  label="Company"
                  name="company_id"
                  rules={[{ required: true, message: "Company is required." }]}
                >
                  <Select
                    placeholder="Select company"
                    showSearch
                    allowClear
                    options={companies.map((c: any) => ({ value: c.id, label: c.name }))}
                    filterOption={(input, option) =>
                      typeof option?.label === "string" &&
                      option.label.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              </Col>

              {/* Amount */}
              <Col xs={24} md={12}>
                <Form.Item
                  label="Amount"
                  name="amount_paid"
                  rules={[{ required: true, message: "Amount paid is required." }]}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    min={0}
                    precision={2}
                    placeholder="0.00"
                    formatter={(value) =>
                      `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    }
                    parser={(value) => value?.replace(/,/g, "") as any}
                  />
                </Form.Item>
              </Col>

              {/* Type */}
              <Col xs={24} md={12}>
                <Form.Item
                  label="Type"
                  name="type"
                  rules={[{ required: true, message: "Type is required." }]}
                >
                  <Select
                    placeholder="Select type"
                    allowClear
                    options={typeOptions}
                  />
                </Form.Item>
              </Col>

              {/* Actions */}
              <Col xs={24} className="flex justify-end gap-3 mt-2">
                <Button
                  onClick={() => {
                    clearFields();
                    setShowModal("");
                  }}
                >
                  Cancel
                </Button>
                {showModal === "update" && (
                  <Popconfirm
                    title="Are you sure you want to delete this record?"
                    onConfirm={() => deleteAdvancePayment()}
                    okText="Yes"
                  >
                    <Button danger>Delete</Button>
                  </Popconfirm>
                )}
                <Button
                  htmlType="submit"
                  type="primary"
                  loading={loading === "submit"}
                >
                  Submit
                </Button>
              </Col>

            </Row>
          </Form>
        </div>
      </Modal>

      {/* Error Modal */}
      <ErrorModal
        showModal={!!errorMessage}
        onCancel={() => setErrorMessage("")}
        title="Action Failed"
        body={errorMessage}
      />

      {/* Success Modal */}
      <SuccessModal
        showModal={!!successMessage}
        onCancel={() => setSuccessMessage("")}
        title="Success"
        body={successMessage}
      />

      <PersistentFooter />
    </Layout>
  );
};

export default AdvancePayment;